import { atom } from 'jotai'

import { api } from '../lib/api'

export type Message = {
  id: string
  matchId: string
  senderProfileId: string
  body: string
  createdAt: string
  readAt?: string
}

export const messagesAtom = atom<Message[]>([])
export const messagesMatchIdAtom = atom<string | null>(null)
export const messagesIsLoadingAtom = atom(false)
export const messagesErrorAtom = atom<string | null>(null)
export const messagesSendIsLoadingAtom = atom(false)

export const fetchMessagesAtom = atom(
  null,
  async function fetchMessages(_get, set, matchId: string) {
    set(messagesIsLoadingAtom, true)
    set(messagesErrorAtom, null)
    set(messagesMatchIdAtom, matchId)

    try {
      const response = await api.fetch(`/matches/${matchId}/messages`)
      if (!response.ok) {
        set(messagesErrorAtom, 'Failed to load messages')
        set(messagesIsLoadingAtom, false)
        return
      }

      const data = (await response.json()) as { messages: Message[] }
      set(messagesAtom, data.messages ?? [])
      set(messagesIsLoadingAtom, false)
    } catch {
      set(messagesErrorAtom, 'Network error')
      set(messagesIsLoadingAtom, false)
    }
  },
)

export const sendMessageAtom = atom(
  null,
  async function sendMessage(get, set, payload: { matchId: string; body: string }) {
    set(messagesSendIsLoadingAtom, true)
    set(messagesErrorAtom, null)

    try {
      const response = await api.fetch(`/matches/${payload.matchId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: payload.body }),
      })

      if (!response.ok) {
        set(messagesErrorAtom, 'Failed to send message')
        set(messagesSendIsLoadingAtom, false)
        return { success: false as const }
      }

      const message = (await response.json()) as Message
      const existing = get(messagesAtom)
      set(messagesAtom, [...existing, message])
      set(messagesSendIsLoadingAtom, false)
      return { success: true as const, message }
    } catch {
      set(messagesErrorAtom, 'Network error')
      set(messagesSendIsLoadingAtom, false)
      return { success: false as const }
    }
  },
)

export const clearMessagesAtom = atom(null, function clearMessages(_get, set) {
  set(messagesAtom, [])
  set(messagesMatchIdAtom, null)
  set(messagesErrorAtom, null)
})
